// Frontend permissions - reads the in-memory session snapshot maintained by
// AuthProvider. No tokens or user state are ever read from localStorage or
// sessionStorage; the source of truth is the `cc.sid` session cookie and the
// derived `UserDto` returned by `/api/auth/me`.
import { PermissionArea, UserDto } from '@cloudcost/shared-types/users';
import {
  getUser as getFrontendUser,
  setUserSession,
} from './common';
import { permissionService } from '@cloudcost/shared-types/permissions-core';
import {urls} from '@/urls'
import { fetchWrapper } from '@/lib/fetch-wrapper';

export const hasUserAccessTo = (
  area: PermissionArea,
  userId?: number,
): boolean => {
  try {
    const currentUser = getFrontendUser();

    if (!currentUser) {
      return false;
    }

    if (area === PermissionArea.SPECIAL_IS_SYSTEM_OWNER) {
      return Boolean(currentUser.is_systemOwner);
    }

    if (area === PermissionArea.SPECIAL_IS_DEPARTMENT_OWNER) {
      return Boolean(currentUser.is_departmentOwner);
    }

    if (area === PermissionArea.SPECIAL_IS_RESOURCE_OWNER) {
      return Boolean(currentUser.is_resourceOwner);
    }

    if (area === PermissionArea.SPECIAL_IS_CLOUD_ACCOUNT_OWNER) {
      return Boolean(currentUser.is_cloudAccountOwner);
    }

    // Map our slim in-memory snapshot onto the full `UserDto` shape that the
    // shared permission service expects. The unknown profile fields are left
    // empty — permission rules don't read them.
    const userDto: UserDto = {
      id: currentUser.id || 0,
      firstName: currentUser.firstName || '',
      lastName: '',
      username: currentUser.username || '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      email: '',
      photo_path: null,
      default_currency: currentUser.default_currency || 'USD',
      is_technical: Boolean(currentUser.is_technical),
      is_admin: Boolean(currentUser.is_admin),
      is_business: Boolean(currentUser.is_business),
      is_sponsor: Boolean(currentUser.is_sponsor),
      is_departmentOwner: Boolean(currentUser.is_departmentOwner),
      is_systemOwner: Boolean(currentUser.is_systemOwner),
      is_resourceOwner: Boolean(currentUser.is_resourceOwner),
      is_cloudAccountOwner: Boolean(currentUser.is_cloudAccountOwner),
    };

    const result = permissionService.hasUserAccessTo(area, userDto, userId);

    return result;
  } catch (error) {
    console.error('Permission check error:', error);
    return false;
  }
};

export const checkSpecialPermission = async (
  area: PermissionArea,
): Promise<boolean> => {
  try {
    const currentUser = getFrontendUser();

    if (!currentUser) {
      return false;
    }

    if (area === PermissionArea.SPECIAL_IS_SYSTEM_OWNER) {
      return Boolean(currentUser.is_systemOwner);
    }

    if (area === PermissionArea.SPECIAL_IS_DEPARTMENT_OWNER) {
      return Boolean(currentUser.is_departmentOwner);
    }

    if (area === PermissionArea.SPECIAL_IS_RESOURCE_OWNER) {
      return Boolean(currentUser.is_resourceOwner);
    }

    if (area === PermissionArea.SPECIAL_IS_CLOUD_ACCOUNT_OWNER) {
      return Boolean(currentUser.is_cloudAccountOwner);
    }

    return false;
  } catch (error) {
    console.error('Permission check error:', error);
    return false;
  }
};

// Consolidated permission fetching function. Reads the in-memory snapshot
// instead of localStorage. The snapshot is kept in sync with the server by
// `refreshUserRoles` and by AuthProvider on initial mount.
export const fetchAllSpecialPermissions = async (
  _userId: number,
): Promise<{
  isResourceOwner: boolean;
  isCloudAccountOwner: boolean;
  isDepartmentOwner: boolean;
  isSystemOwner: boolean;
}> => {
  try {
    const snapshot = getFrontendUser();
    return {
      isResourceOwner: Boolean(snapshot?.is_resourceOwner),
      isCloudAccountOwner: Boolean(snapshot?.is_cloudAccountOwner),
      isDepartmentOwner: Boolean(snapshot?.is_departmentOwner),
      isSystemOwner: Boolean(snapshot?.is_systemOwner),
    };
  } catch (error) {
    console.error('Error fetching special permissions:', error);
    return {
      isResourceOwner: false,
      isCloudAccountOwner: false,
      isDepartmentOwner: false,
      isSystemOwner: false,
    };
  }
};

export const refreshUserRoles = async () => {
  try {
    const data = await fetchWrapper.get(urls.DevOps.getUserRoles);
    const current = getFrontendUser();
    if (!current) return;
    setUserSession({
      id: current.id,
      firstName: current.firstName,
      username: current.username,
      default_currency: current.default_currency,
      is_admin: Boolean(data.is_admin),
      is_technical: Boolean(data.is_technical),
      is_business: Boolean(data.is_business),
      is_sponsor: Boolean(data.is_sponsor),
      is_departmentOwner: Boolean(data.is_departmentOwner),
      is_systemOwner: Boolean(data.is_systemOwner),
      is_resourceOwner: Boolean(data.is_resourceOwner),
      is_cloudAccountOwner: Boolean(data.is_cloudAccountOwner),
    } as any);
  } catch (error) {
    console.error('There was an error refreshing user roles!', error);
  }
};

export { permissionService };
